import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
  Grid,
  TextField,
  IconButton,
  SearchIcon,
  MyLocationIcon,
  ExpandMoreIcon,
  Typography,
  Autocomplete,
  ListItemText,
  ListItemIcon,
  ArrowDropDownIcon,
  CircularProgress,
  Link,
  styledComponent,
  MenuItem,
  MenuList,
  Stack,
  Paper,
  useTheme,
  useMediaQuery,
  ClickAwayListener
} from '@hermes/web-components'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import { SearchProfilesPageContext } from '../../../../providers/SearchProfilesPage'
import { AppDataContext } from '../../../../providers/AppData'
import { useTimeout } from '../../../../hooks/useTimeout'
import useClickOutside from '../../../../hooks/useClickOutside'
import { ProfileTypes, SearchTypes } from '../../../../providers/SearchProfilesPage/types'
import { MappedLocation, WebViewProps } from '../../common/types'
import { Location } from '../../../../types'
import CustomAutocomplete from './CustomAutocomplete'
import { TEST_IDS } from '../../../../__tests__/testUtils/constants'
import useSearchBarConfig from '../../../search/hooks/useSearchBarConfig'
import ConditionalRenderBox from './ConditionalRenderBox'
import NextImage from 'next/image'

const whiteColor = 'common.white'

const StyledIconButton = styledComponent(IconButton)(() => ({
  width: '100%',
  height: '100%',
  borderRadius: '32px',
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
  backgroundColor: whiteColor,
  padding: 0,
  margin: 0
}))

const CustomMenuItem = styledComponent(MenuItem)(() => ({
  border: '1px solid',
  borderColor: 'blue.300',
  borderRadius: '32px',
  boxShadow: 'none',
  '.MuiMenuItem-root': {
    borderRadius: '8px',
    borderBottom: 'none',
    '&.Mui-selected': {
      pointerEvents: 'none',
      backgroundColor: '#F4F5F5',
      '.MuiTypography-root': {
        fontWeight: '500'
      }
    },
    '&:hover': {
      backgroundColor: 'grey.100'
    }
  }
}))

const StyledAutocomplete = styledComponent(Autocomplete)`
  & .MuiInputBase-input {
    height: 27px;
  },
`

const LocationSearchTextField = styledComponent(TextField)({
  height: '100%',
  borderTopRightRadius: '32px',
  borderBottomRightRadius: '32px',
  '.MuiInputBase-root': {
    height: '100%',
    padding: '0 85px 0 0',
    input: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap'
    }
  }
})

const WebView = ({
  onChangeSearchType,
  onChangeSearch,
  onSearchFieldFocus,
  onSearchFieldBlur,
  onChangeLocationSearch,
  getYourLocation,
  isClientSide,
  isLocationFieldFocused,
  setIsLocationFieldFocused,
  ...autocompleteProps
}: WebViewProps) => {
  const { language } = useContext(AppDataContext)
  const translate = useTranslateMessage()
  const {
    changeCurrentLocation,
    buildUrl,
    resetLocationsAutocomplete,
    state: { isLoading, locations, filters, specialty, currentLocation }
  } = useContext(SearchProfilesPageContext)

  const { searchTypes, selectedSearchType } = useSearchBarConfig()

  const locationsAutocompleteRef = useRef(null)
  const SelectedIcon = selectedSearchType?.Icon

  const autocompleteLocationMapper = ({
    place_id,
    description,
    name,
    googlePlaceId,
    slug
  }: Location): MappedLocation => ({
    id: place_id || googlePlaceId,
    label: description || name[language],
    slug
  })

  const mappedLocation: MappedLocation[] = locations?.map(autocompleteLocationMapper) || []
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))

  const menuContainer = useRef(null)
  const [openSearchTypeDropdown, setOpenSearchTypeDropdown] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [isNewLocationSelected, setIsNewLocationSelected] = useState(false)
  const handleSetFocusOnLocation = () => setIsLocationFieldFocused(!isLocationFieldFocused)
  useClickOutside(locationsAutocompleteRef, () => {
    if (isDesktop) {
      resetLocationsAutocomplete()
    }
    setIsLocationFieldFocused(false)
  })
  const onOpen = () => setOpenSearchTypeDropdown(!openSearchTypeDropdown)
  const onClose = () => setOpenSearchTypeDropdown(false)
  useClickOutside(menuContainer, onClose)
  const timeout = useTimeout()

  const onLocationChanged = (newValue: { label: string; id: string; slug?: string | null }) =>
    timeout(() => {
      setIsNewLocationSelected(true)
      changeCurrentLocation(newValue.id, newValue.label, newValue.slug)
    })

  const handleSearch = () => {
    if (!isNewLocationSelected) {
      // eslint-disable-next-line no-restricted-globals
      location.reload()
    }
  }

  const handleSearchType = useCallback(
    (value: SearchTypes) => () => {
      onClose()
      onChangeSearchType(value)
    },
    [onChangeSearchType]
  )

  useEffect(() => {
    if (!searchText) {
      setSearchText(decodeURIComponent(filters.location))
    }
    // todo: clarify deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.location])

  return (
    <>
      <Grid sx={{ ...(theme?.custom?.SearchBar?.profileTypeContainer ?? {}) }} item sm={3.5}>
        <ClickAwayListener onClickAway={onClose}>
          <StyledIconButton
            data-testid={TEST_IDS.SEARCH_BAR_PROFILE_TYPE_SELECTOR}
            sx={{
              cursor: 'pointer',
              position: 'relative',
              backgroundColor: whiteColor
            }}
            onClick={onOpen}
            disableRipple
          >
            {selectedSearchType && (
              <CustomMenuItem
                key={'search-type-button'}
                value={''}
                sx={{
                  backgroundColor: whiteColor,
                  width: '100%',
                  borderColor: 'blue.300',
                  borderWidth: 1,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  padding: 2
                }}
              >
                <ListItemIcon>
                  {SelectedIcon && (
                    <NextImage
                      src={`/hermes-static/icons/${SelectedIcon}.svg`}
                      alt="search-type-icon"
                      width={22}
                      height={22}
                      objectFit="none"
                      unoptimized
                    />
                  )}
                </ListItemIcon>
                <ListItemText>
                  <Typography color="text.primary" variant="body2" textAlign="left">
                    {selectedSearchType.title}
                  </Typography>
                </ListItemText>
                <ListItemIcon>
                  <ArrowDropDownIcon
                    color="primary"
                    sx={{ transform: openSearchTypeDropdown ? 'rotate(180deg)' : 'rotate(0)', ml: 2 }}
                  />
                </ListItemIcon>
              </CustomMenuItem>
            )}
            <MenuList
              ref={menuContainer}
              sx={{
                width: '90%',
                border: '1px solid rgb(191, 205, 246)',
                display: openSearchTypeDropdown ? 'block' : 'none',
                position: 'absolute',
                top: '100%',
                left: '10%'
              }}
            >
              {searchTypes.map(({ value, slug, title, Icon }) => {
                const link = buildUrl({
                  slug: currentLocation?.slug,
                  location: currentLocation?.name[language],
                  profileType: value === SearchTypes.specialists ? ProfileTypes.specialists : ProfileTypes.practices,
                  keywordSlug: slug
                })
                const isSelected = selectedSearchType?.value === value
                return (
                  <Link
                    key={`search-type-${value}`}
                    sx={{
                      textDecoration: 'none',
                      textDecorationColor: '-moz-initial',
                      color: 'text.primary'
                    }}
                    href={link}
                  >
                    <MenuItem
                      value={value}
                      sx={{ backgroundColor: whiteColor, width: '100%', padding: 0.75 }}
                      onClick={handleSearchType(value)}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        width="100%"
                        sx={{
                          backgroundColor: isSelected ? theme.palette.background.default : 'transparent',
                          borderRadius: 3,
                          p: 0.5
                        }}
                      >
                        <Stack direction="row">
                          <ListItemIcon>
                            <NextImage
                              src={`/hermes-static/icons/${Icon}.svg`}
                              alt="search-type-icon"
                              width={22}
                              height={22}
                              objectFit="none"
                              unoptimized
                            />
                          </ListItemIcon>
                          <ListItemText>
                            <Typography color="text.primary" variant="body1" textAlign="left">
                              {title}
                            </Typography>
                          </ListItemText>
                        </Stack>
                        <ExpandMoreIcon
                          className="checkIcon"
                          color="primary"
                          // @ts-ignore
                          sx={{
                            fontSize: '22px',
                            transform: 'rotate(-90deg)',
                            fill: isSelected ? theme.palette.primary : theme.palette.background.default
                          }}
                        />
                      </Stack>
                    </MenuItem>
                  </Link>
                )
              })}
            </MenuList>
          </StyledIconButton>
        </ClickAwayListener>
      </Grid>
      <Grid container item sm={10} position="relative" width="100%">
        <Grid item sm={7}>
          {/* fake autocomplete to avoid circular text field update */}
          <StyledAutocomplete
            data-testid={TEST_IDS.SEARCH_BAR_CONDITION_SEARCH}
            options={[]}
            disablePortal={true}
            disableClearable={true}
            popupIcon={null}
            value={specialty.name[language]}
            renderInput={(props) => (
              <TextField
                {...props}
                fullWidth
                placeholder={translate('search.placeholder.type_to_search')}
                onChange={onChangeSearch}
                value={specialty.name[language]}
                onFocus={onSearchFieldFocus}
                onBlur={onSearchFieldBlur}
                InputProps={{
                  sx: {
                    borderRadius: 0,
                    '.MuiOutlinedInput-notchedOutline': { borderColor: 'blue.300', borderLeftWidth: 0 }
                  }
                }}
              />
            )}
          />
        </Grid>
        <Grid sx={{ ...(theme?.custom?.SearchBar?.locationContainer ?? {}) }} item sm={5} position="relative">
          <LocationSearchTextField
            data-testid={TEST_IDS.SEARCH_BAR_LOCATIONS_SEARCH}
            ref={locationsAutocompleteRef}
            fullWidth
            value={searchText}
            autoComplete="off"
            onFocus={handleSetFocusOnLocation}
            placeholder={translate('search.placeholder.location')}
            onChange={(e) => {
              setIsLocationFieldFocused(true)
              setSearchText(e.target.value)
              onChangeLocationSearch(e)
            }}
            onBlur={() => {
              setSearchText(decodeURIComponent(filters.location))
            }}
            InputProps={{
              sx: {
                borderRadius: '32px',
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                '.MuiOutlinedInput-notchedOutline': { borderColor: 'blue.300', borderLeftWidth: 0 }
              },
              endAdornment: (
                <ConditionalRenderBox
                  isOpen={isLocationFieldFocused}
                  isClientSide={isClientSide}
                  sx={{
                    position: 'absolute',
                    top: '60px',
                    width: '100%'
                  }}
                >
                  <Paper
                    sx={{
                      padding: '8px 0px',
                      maxHeight: '40vh',
                      overflowY: 'auto'
                    }}
                  >
                    {mappedLocation.length ? (
                      mappedLocation.map((x) => {
                        const url = buildUrl({
                          location: x.label,
                          slug: x.slug
                        })
                        return (
                          <MenuItem
                            component="a"
                            key={`location-link-${x.id}-${x.label}`}
                            href={url}
                            onClick={() => onLocationChanged(x)}
                            sx={{
                              fontSize: '14px',
                              padding: '6px 16px',
                              color: 'text.primary',
                              boxSizing: 'border-box',
                              display: 'flex',
                              justifyContent: 'flex-start',
                              whiteSpace: 'wrap'
                            }}
                          >
                            {x.label}
                          </MenuItem>
                        )
                      })
                    ) : (
                      <Typography
                        sx={{
                          color: 'rgb(76, 88, 112)',
                          padding: '6px 16px'
                        }}
                      >
                        {translate('searchBar.noOption')}
                      </Typography>
                    )}
                  </Paper>
                </ConditionalRenderBox>
              )
            }}
          />
          <MyLocationIcon
            data-testid={TEST_IDS.SEARCH_BAR_MY_LOCATION_BUTTON}
            onClick={getYourLocation}
            sx={{
              color: 'primary.main',
              width: '20px',
              height: '20px',
              position: 'absolute',
              cursor: 'pointer',
              right: theme?.custom?.SearchBar?.myLocationIcon?.right ?? 60,
              top: 20,
              borderRadius: '50%'
            }}
          />
          <IconButton
            data-testid={TEST_IDS.SEARCH_BAR_SEARCH_BUTTON}
            onClick={handleSearch}
            sx={{ ...(theme?.custom?.SearchBar?.searchButton ?? {}) }}
          >
            {isLoading ? (
              <CircularProgress
                sx={{
                  color: theme?.custom?.SearchBar?.searchButton?.color ?? 'white',
                  width: '1rem !important',
                  height: '1rem !important'
                }}
              />
            ) : (
              <SearchIcon sx={{ color: theme?.custom?.SearchBar?.searchButton?.color ?? whiteColor }} />
            )}
          </IconButton>
        </Grid>
        <Grid item sm={7} position="relative">
          <CustomAutocomplete
            onChangeSearch={onChangeSearch}
            onSearchFieldBlur={onSearchFieldBlur}
            isLocationFieldFocused={isLocationFieldFocused}
            setIsLocationFieldFocused={setIsLocationFieldFocused}
            {...autocompleteProps}
          />
        </Grid>
      </Grid>
    </>
  )
}

export default WebView
